import React, { useContext, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import IUserManagement from '../user-management.interface'
import UserContext from '../user-context'
import { IUserData } from '../user/user.interface'
import User from '../user/user'
import Spinner from '../../spinner/spinner'
import DashboardContext from '../../layout/dashboard-context'
import MessageContext from '../../message/message-context'
import api from '../../../api'
import RoleInfo from '../role-info'

const EditUser: React.FC<IUserManagement> = () => {
  const { contactUsers, setContactUsers } = useContext(UserContext)
  const { modifiedUsers, setModifiedUsers } = useContext(UserContext)
  const { selectedContact, setSelectedContact } = useContext(UserContext)
  const { selectedOrganization } = useContext(DashboardContext)
  const { setMessage, setErrorMessage } = useContext(MessageContext)

  const [isLoading, setIsLoading] = useState(false)
  const [name, setName] = useState(selectedContact.name)
  const [loading, setLoading] = useState(false)
  const [showUpdatedText, setShowUpdatedText] = useState(false)

  const addNewChange = (id: string) => {
    if (id && id.length > 0) {
      if (!modifiedUsers.some((element) => element === id)) {
        modifiedUsers.push(id)
      }
    }
  }
  const updateAllChanged = async () => {
    setLoading(true)
    showUpdatedText && setShowUpdatedText(false)
    try {
      for (const user in modifiedUsers) {
        const id = modifiedUsers[user]
        const found = contactUsers.find((element) => element.id === id)
        if (found) {
          await updateUser(found)
        }
      }
      for (const user in contactUsers) {
        const row = contactUsers[user]
        if (row.id === '') {
          const created = await createUser(row)
          contactUsers[user].id = created.data.id
        }
      }
      if (name !== selectedContact.name) {
        await updateContact(name)
      }
      setModifiedUsers([])
    } catch (e) {
      setErrorMessage('Något gick fel vid uppdatering av användaren')
    }
    setLoading(false)
  }

  const addNewEmptyRow = () => {
    const tempArray = contactUsers.concat()
    const newField = {
      id: '',
      name: selectedContact.name,
      contactRole: '',
      contactId: selectedContact.id,
    }
    tempArray.push(newField)
    setContactUsers(tempArray)
  }

  const deleteRow = async (id: string, index: number) => {
    showUpdatedText && setShowUpdatedText(false)
    setLoading(true)
    let res = {
      data: {
        id: '',
      },
    }
    try {
      if (id !== '') {
        res = await deleteUser(id)
      }
    } catch (e) {
      setErrorMessage('Något gick fel vid uppdatering av användaren')
    }
    if ((res.data && res.data.id) || id === '') {
      if (index >= 0) {
        const tempArray = contactUsers.concat()
        tempArray.splice(index, 1)
        setContactUsers(tempArray)
      }
    }
    setLoading(false)
  }

  const returnToContactList = () => {
    setSelectedContact({
      id: '',
      name: '',
    })
    setContactUsers([])
  }

  const getContact = async () => {
    try {
      const res = await api.get(
        `/accounts/${selectedOrganization.id}/contacts/${selectedContact.id}`,
      )

      return res.data.data
    } catch (e) {
      return []
    }
  }

  const updateContact = async (name: string) => {
    showUpdatedText && setShowUpdatedText(false)
    try {
      const res = await api.put(
        `/accounts/${selectedOrganization.id}/contacts/${selectedContact.id}`,
        {
          name,
        },
      )

      if (res && res.status === 200) {
        setShowUpdatedText(true)
        return res.data.data
      }

      return false
    } catch (e) {
      return false
    }
  }

  const updateUser = async (user: IUserData) => {
    showUpdatedText && setShowUpdatedText(false)
    try {
      const res = await api.put(`/accounts/${selectedOrganization.id}/users/${user.id}`, user)

      if (res.status === 400) {
        setErrorMessage(
          'Kontaktroll kunde inte uppdateras. Antingen har den nya rollen tilldelats för många kontakter (max 2st), eller så försöker du ta bort en roll som måste finnas med i varje organisation.',
        )
        return []
      }

      if (res && res.status === 200) {
        setMessage({ value: 'Användare uppdaterades' })
        setShowUpdatedText(true)
        return res.data
      } else {
        setErrorMessage('Något gick fel vid uppdatering av användaren')
        return false
      }
    } catch (e) {
      setErrorMessage('Något gick fel vid uppdatering av användaren')
      return []
    }
  }

  const deleteUser = async (id: string) => {
    showUpdatedText && setShowUpdatedText(false)
    try {
      const res = await api.delete(`/accounts/${selectedOrganization.id}/users/${id}`)

      if (res.status === 400) {
        setErrorMessage(
          'Rollen kunde inte uppdateras. Detta kan bero på att det är den enda kontakten på nuvarande roll, eller att den nya rollen har för många kontakter.',
        )
        return []
      }

      setShowUpdatedText(true)
      return res.data
    } catch (e) {
      return []
    }
  }

  const createUser = async (user: IUserData) => {
    try {
      const res = await api.post(`/accounts/${selectedOrganization.id}/users`, user)

      if (res.status === 400) {
        setErrorMessage('Max 2 personer per organisation får ha denna roll.')
        return []
      }

      setShowUpdatedText(true)
      return res.data
    } catch (e) {
      return []
    }
  }

  useEffect(() => {
    setContactUsers([])
    setIsLoading(true)
    const getUserData = async () => {
      try {
        const contactData = await getContact(selectedContact)
        setContactUsers(contactData)
      } catch (e) {
        setErrorMessage('Något gick fel vid hämtning av användare')
      }
      setIsLoading(false)
    }
    getUserData()
    return () => {
      setContactUsers([])
    }
  }, [])

  const handleDeleteContact = async () => {
    const contact = selectedContact
    if (contact && window.confirm(`Är du säker på att du vill ta bort kontakt ${contact.name}?`)) {
      const res = await deleteContact(contact.id)
      if (res && res.id) {
        returnToContactList()
      }
    }
  }

  const deleteContact = async (id: string) => {
    try {
      const res = await api.delete(`/accounts/${selectedOrganization.id}/contacts/${id}`)

      if (res.status === 400) {
        setErrorMessage(
          'Kontakten kunde inte raderas. Denna kontakt är den enda med denna roll. Vänligen tilldela rollen till någon annan innan kontakten kan raderas.',
        )
      }

      return res.data.data
    } catch (e) {
      return []
    }
  }

  return (
    <div className="card p-12 flex flex-col w-full bg-white items-center">
      <div className="flex justify-between w-3/4 flex-col mb-8">
        <div className="flex justify-between w-full flex-row">
          <h1>Hantera användare</h1>
          <button className="btn-white btn-sm" onClick={() => handleDeleteContact()}>
            <FontAwesomeIcon icon={faTrash} color="#003D64" />
            <span className="ml-2"> Radera användare </span>
          </button>
        </div>
        <RoleInfo />
      </div>

      <div className="w-3/4 flex flex-col gap-3">
        <div className="w-full">
          <label className="flex flex-col justify-start items-start gap-2 w-11/12">
            <span className="label">Namn:</span>
            <input
              className="input"
              defaultValue={selectedContact.name}
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
          </label>
        </div>
        {!isLoading && contactUsers && contactUsers.length > 0 ? (
          <table className="text-left w-full">
            <thead>
              <tr className="flex w-full">
                <th className="label">Kontaktroll</th>
              </tr>
            </thead>
            <tbody className="flex flex-col items-center justify-between w-full gap-2">
              {contactUsers && contactUsers.length > 0
                ? contactUsers.map((user, index) => {
                    return (
                      <User
                        key={`${user.id}_${index}`}
                        data={user}
                        index={index}
                        addNewChange={addNewChange}
                        deleteRow={deleteRow}
                      />
                    )
                  })
                : null}
            </tbody>
          </table>
        ) : !isLoading ? (
          <p className="text-darkBlue">Användaren har inga kontaktroller.</p>
        ) : null}
        {isLoading ? (
          <div className="m-40">
            <Spinner />
          </div>
        ) : null}
        <div>
          <button
            className="text-darkBlue font-sans text-sm font-bold py-2 mr-10 rounded"
            onClick={() => addNewEmptyRow()}
          >
            + Lägg till ny roll
          </button>
        </div>
        <div className="flex justify-between items-center w-full mt-4 md:mt-0">
          <button className="btn-white" onClick={() => returnToContactList()}>
            Tillbaka
          </button>
          <div className="self-end flex flex-row py-2">
            {loading ? (
              <div>
                <Spinner />
              </div>
            ) : (
              <button className="btn btn-long" onClick={() => updateAllChanged()}>
                Spara
              </button>
            )}
          </div>
          {showUpdatedText && (
            <div id="popup-modal" className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div className="relative transform overflow-hidden rounded-lg bg-tabGray text-left shadow-xl transition-all sm:my-8 sm:max-w-lg">
                  <div className="center gap-10 items-center flex flex-col p-10">
                    <h2 className="text-xl font-semibold text-darkBlue">
                      Dina ändringar har blivit sparade!
                    </h2>
                    <button
                      onClick={() => setShowUpdatedText(false)}
                      className="bg-darkBlue text-fastigoWhite font-sans text-sm font-bold py-2 px-4 rounded"
                    >
                      Okej
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditUser
